import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { UserListRequestPayload, UserMutationPayload } from './types';
import { COMPANY_API_SETTINGS_MOCKED } from '@/env';
import { QUERY_KEY_SETTINGS_ADMIN_PANEL } from '@/lib/api/constants';
import { createAdminUser } from './createAdminUser';

type Props = {
  filters: UserListRequestPayload
  payload: UserMutationPayload
}

type CreateUserErrorItem = {
  success?: boolean
  Success?: boolean
  code?: string
  Code?: string
  message?: string
  Message?: string
  path?: string
  Path?: string
}

type CreateUserResponse = CreateUserErrorItem | CreateUserErrorItem[]

export class CreateUserError extends Error {
  code: string
  path?: string

  constructor(code: string, message: string, path?: string) {
    super(message)
    this.code = code
    this.path = path
    this.name = 'CreateUserError'
  }
}

function extractErrorFromResponse(data: CreateUserResponse | undefined): { code?: string; message?: string; path?: string } {
  if (!data) return {}
  const item = Array.isArray(data) ? data[0] : data
  if (!item) return {}
  return {
    code: item.code || item.Code,
    message: item.message || item.Message,
    path: item.path || item.Path,
  }
}

export function useMutationAdminUserCreate(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation<CreateUserResponse, CreateUserError | Error, Props>({
    mutationFn: async ({ payload }: Props) => {
      if (COMPANY_API_SETTINGS_MOCKED) {
        return { success: true }
      }

      try {
        const { data } = await createAdminUser({ companyId, payload })

        // Backend might return 200 with error details
        const { code, message, path } = extractErrorFromResponse(data as CreateUserResponse)
        const item = Array.isArray(data) ? data[0] : data
        const success = item?.success ?? item?.Success

        if (success === false || code) {
          throw new CreateUserError(code || 'UNKNOWN_ERROR', message || 'Unknown error', path)
        }

        return data as CreateUserResponse
      } catch (error) {
        // Re-throw CreateUserError as-is
        if (error instanceof CreateUserError) {
          throw error
        }
        // Handle axios errors (4xx, 5xx responses)
        if (axios.isAxiosError(error) && error.response?.data) {
          const responseData = error.response.data as CreateUserResponse
          const { code, message, path } = extractErrorFromResponse(responseData)
          if (code || message) {
            throw new CreateUserError(code || 'API_ERROR', message || error.message, path)
          }
        }
        // Re-throw other errors
        throw error
      }
    },

    onSuccess: (_, { filters }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_SETTINGS_ADMIN_PANEL, 'users', companyId, filters] });
    },
  });
}
